'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Loader2, Mail, CheckCircle2, RefreshCw, ArrowRight, X } from 'lucide-react'
import { authClient } from '@/lib/auth'

const OTP_LENGTH = 6
const RESEND_COOLDOWN = 120

export interface VerifyEmailDialogProps {
  isOpen?: boolean
  open?: boolean
  onClose?: () => void
  onOpenChange?: (open: boolean) => void
  email: string
  purpose?: string
  deviceId?: string
  deviceName?: string
  onSuccess: (data?: any) => void
}

export const VerifyEmailDialog: React.FC<VerifyEmailDialogProps> = ({
  isOpen,
  open,
  onClose,
  onOpenChange,
  email,
  purpose = 'signup',
  deviceId,
  deviceName,
  onSuccess,
}) => {
  const isModalOpen = isOpen !== undefined ? isOpen : (open !== undefined ? open : false)
  const handleClose = () => {
    if (onClose) onClose()
    if (onOpenChange) onOpenChange(false)
  }

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN)
  const [verified, setVerified] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Reset state when dialog opens
  useEffect(() => {
    if (isModalOpen) {
      setDigits(Array(OTP_LENGTH).fill(''))
      setCooldown(RESEND_COOLDOWN)
      setVerified(false)
      setErrorMessage(null)
      setTimeout(() => {
        inputRefs.current[0]?.focus()
      }, 150)
    }
  }, [isModalOpen])

  // Cooldown countdown timer
  useEffect(() => {
    if (!isModalOpen || cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(timer)
  }, [isModalOpen, cooldown])

  const handleDigitChange = (index: number, value: string) => {
    const char = value.replace(/\D/g, '').slice(-1)
    const nextDigits = [...digits]
    nextDigits[index] = char
    setDigits(nextDigits)
    setErrorMessage(null)

    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // If all digits entered, auto submit
    if (char && index === OTP_LENGTH - 1) {
      const fullCode = nextDigits.join('')
      if (fullCode.length === OTP_LENGTH) {
        submitOtp(fullCode)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return

    const nextDigits = [...digits]
    for (let i = 0; i < OTP_LENGTH; i++) {
      nextDigits[i] = pasted[i] || ''
    }
    setDigits(nextDigits)
    setErrorMessage(null)

    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusIdx]?.focus()

    if (pasted.length === OTP_LENGTH) {
      submitOtp(pasted)
    }
  }

  const submitOtp = useCallback(
    async (codeToSubmit?: string) => {
      const code = codeToSubmit || digits.join('')
      if (code.length < OTP_LENGTH) {
        setErrorMessage(`Please enter all ${OTP_LENGTH} digits.`)
        return
      }

      setLoading(true)
      setErrorMessage(null)

      try {
        const result = purpose === 'device_verification'
          ? await authClient.verifyDeviceOtp({ email, code, deviceId, deviceName })
          : await authClient.verifyOtp(email, code)

        if (result && (result.success || result.status === 'success' || result.data?.success)) {
          setVerified(true)
          setTimeout(() => {
            onSuccess(result?.data || result)
          }, 900)
        } else {
          const err = result?.error || result?.message || 'Invalid or expired verification code.'
          setErrorMessage(err)
          setDigits(Array(OTP_LENGTH).fill(''))
          inputRefs.current[0]?.focus()
        }
      } catch (err: any) {
        setErrorMessage(err?.message || 'Verification failed. Please check your connection and try again.')
      } finally {
        setLoading(false)
      }
    },
    [digits, email, deviceId, deviceName, purpose, onSuccess]
  )

  const handleResend = async () => {
    if (cooldown > 0 || resending) return

    setResending(true)
    setErrorMessage(null)

    try {
      const res = await authClient.requestOtp(email, purpose)
      if (res && (res.success || res.status === 'success')) {
        setCooldown(RESEND_COOLDOWN)
        setDigits(Array(OTP_LENGTH).fill(''))
        inputRefs.current[0]?.focus()
      } else {
        setErrorMessage(res?.error || res?.message || 'Failed to resend code. Please try again.')
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Unable to connect to authentication server.')
    } finally {
      setResending(false)
    }
  }

  if (!isModalOpen) return null

  const isComplete = digits.every((d) => d !== '')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading && !verified) {
          handleClose()
        }
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-[#131D31] border border-slate-200 dark:border-slate-800 shadow-xl p-6 sm:p-8 flex flex-col items-center text-center overflow-hidden">
        {/* Subtle top accent */}
        <div className="absolute top-0 inset-x-0 h-1.5 bg-[#0A1B4A]" />

        {/* Close Button */}
        {!verified && (
          <button
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
            className="absolute top-4 right-4 size-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center transition-colors"
          >
            <X className="size-4" />
          </button>
        )}

        {/* Icon */}
        <div className="mb-4 mt-2">
          {verified ? (
            <div className="size-16 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 flex items-center justify-center animate-bounce">
              <CheckCircle2 className="size-9" />
            </div>
          ) : (
            <div className="size-16 rounded-2xl bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 text-[#2563EB] dark:text-blue-400 flex items-center justify-center shadow-xs">
              <Mail className="size-8" />
            </div>
          )}
        </div>

        {/* Title */}
        <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900 dark:text-white tracking-tight">
          {verified ? 'Email Verified Successfully!' : 'Verify Your Email'}
        </h2>

        {/* Description */}
        <p className="font-ui text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-sm leading-relaxed">
          {verified ? (
            'Your buyer account is now verified. Taking you to your Virtual Garage...'
          ) : (
            <>
              We sent a 6-digit verification code to{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-200 break-all">{email}</span>.
              Enter the code below to complete registration.
            </>
          )}
        </p>

        {/* Error Message */}
        {errorMessage && (
          <div className="w-full mt-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-600 dark:text-rose-400 text-xs text-left animate-shake">
            {errorMessage}
          </div>
        )}

        {/* 6-Cell OTP Input */}
        {!verified && (
          <div className="w-full mt-6">
            <div className="flex items-center justify-center gap-2 sm:gap-2.5">
              {digits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputRefs.current[idx] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  disabled={loading}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  onPaste={handlePaste}
                  className={`size-11 sm:size-12 rounded-xl text-center font-mono text-lg sm:text-xl font-bold transition-all focus:outline-none ${
                    digit
                      ? 'border-2 border-[#2563EB] bg-blue-50/40 dark:bg-blue-950/40 text-slate-900 dark:text-white shadow-xs'
                      : 'border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0B132B] text-slate-900 dark:text-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20'
                  }`}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              type="button"
              disabled={loading || !isComplete}
              onClick={() => submitOtp()}
              className="w-full mt-6 py-3 px-4 rounded-xl font-semibold text-sm bg-[#2563EB] hover:bg-blue-600 disabled:opacity-50 text-white flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  <span>Verifying Code...</span>
                </>
              ) : (
                <>
                  <span>Verify &amp; Continue</span>
                  <ArrowRight className="size-4" />
                </>
              )}
            </button>

            {/* Resend Action */}
            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>Didn't receive the code?</span>
              {cooldown > 0 ? (
                <span className="font-mono text-slate-400 dark:text-slate-500">
                  Resend in {Math.floor(cooldown / 60)}:{(cooldown % 60).toString().padStart(2, '0')}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={resending}
                  onClick={handleResend}
                  className="font-semibold text-[#2563EB] dark:text-blue-400 hover:underline flex items-center gap-1 disabled:opacity-50"
                >
                  {resending && <RefreshCw className="size-3 animate-spin" />}
                  <span>Resend Code</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VerifyEmailDialog
